import React, { useEffect, useMemo, useState } from "react";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

/* -----------------------------
   Helpers
--------------------------------*/

// Normalizza i listini prima di inviarli al backend (usato solo da MADRE, tab Catalogo)
const normalizeListini = (listiniInput) => {
  const tipiObbligatori = ["cliente", "franchising", "azienda", "pv"];
  const map = {};
  (listiniInput || []).forEach((l) => {
    map[l.tipo] = {
      tipo: l.tipo,
      prezzo:
        l.prezzo !== undefined && l.prezzo !== null
          ? parseFloat(l.prezzo)
          : 0.0,
      disponibile: l.disponibile ?? false,
    };
  });
  return tipiObbligatori.map(
    (tipo) =>
      map[tipo] || {
        tipo,
        prezzo: 0.0,
        disponibile: false,
      }
  );
};

const getDefaultListinoByRole = (role) => {
  switch ((role || "").toUpperCase()) {
    case "MADRE":
      return "price_client";
    case "AGENTE":
      return "price_azienda";
    case "FRANCHISING":
      return "price_franchising";
    case "AZIENDA":
      return "price_azienda";
    case "PV":
      return "price_pv";
    default:
      return "price_client";
  }
};

const getTipoListinoFromValue = (val) => {
  switch (val) {
    case "price_client":
      return "cliente";
    case "price_franchising":
      return "franchising";
    case "price_azienda":
      return "azienda";
    case "price_pv":
      return "pv";
    default:
      return "cliente";
  }
};

const validTabs = new Set(["catalogo", "local"]);

/* -----------------------------
   Pagina
--------------------------------*/

export default function ProdottiPage() {
  const [prodotti, setProdotti] = useState([]);
  const [caricamento, setCaricamento] = useState(true);

  // Tabs: "catalogo" (MADRE) vs "local" (miei prodotti). Per MADRE niente tab "local".
  const [activeTab, setActiveTab] = useState("catalogo");

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    category: "",
    description: "",
    availability_status: "AVAILABLE",
    unitType: "litri",
    litri_per_unita: "",
    pezzi_per_unita: "",
    listini: [
      { tipo: "cliente", prezzo: "", disponibile: true },
      { tipo: "franchising", prezzo: "", disponibile: true },
      { tipo: "azienda", prezzo: "", disponibile: true },
      { tipo: "pv", prezzo: "", disponibile: true },
    ],
  });

  const [categorie, setCategorie] = useState([]);
  const [listinoAttivo, setListinoAttivo] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [hasFullPackage, setHasFullPackage] = useState(null); // null=non caricato
  // Toolbar (ricerca/filtri/ordinamento)
  const [q, setQ] = useState("");
  const [catSel, setCatSel] = useState("ALL");
  const [soloDisponibili, setSoloDisponibili] = useState(false);
  const [ordina, setOrdina] = useState("name-asc");

  const isMadre = (userRole || "").toUpperCase() === "MADRE";
  const canSeeLocal = !isMadre; // local per tutti tranne MADRE
  const canEditCatalogo = isMadre; // solo MADRE può modificare il catalogo
  const canEditLocal = !isMadre; // tutti gli altri possono creare/gestire i propri prodotti locali

/* --------- Boot: ruolo + deep-link + localStorage --------- */
useEffect(() => {
  const token = localStorage.getItem("access");
  if (!token) return;

  const headers = { Authorization: `Bearer ${token}` };

  const pickRoleAndDefaults = async () => {
    try {
      // ✅ prendiamo ruolo e pacchetto dal backend (fonte unica di verità)
      const r = await fetch(`${API_URL}/api/me/`, { headers });
      const me = await r.json();
      const role = (me?.role || "CLIENTE").toUpperCase();

      setUserRole(role);
      setHasFullPackage(!!me?.has_full_package);

      // leggi querystring
      const qs = new URLSearchParams(window.location.search);
      const qsTab = qs.get("tab");
      const qsListino = qs.get("listino");

      // tab iniziale
      let initialTab =
        (localStorage.getItem("prod_tab") || "").toLowerCase() ||
        (validTabs.has((qsTab || "").toLowerCase()) ? qsTab.toLowerCase() : "") ||
        "catalogo";
      if (role === "MADRE") initialTab = "catalogo"; // forza per MADRE

      // listino iniziale
      const defaultListino = getDefaultListinoByRole(role);
      const storedListino = localStorage.getItem("prod_listino");
      const initialListino =
        (qsListino &&
          ["price_client", "price_franchising", "price_azienda", "price_pv"].includes(qsListino) &&
          qsListino) ||
        storedListino ||
        defaultListino;

      setActiveTab(initialTab);
      setListinoAttivo(initialListino);
    } catch (err) {
      // fallback prudenziale
      setUserRole("CLIENTE");
      setHasFullPackage(false);
      setActiveTab("catalogo");
      setListinoAttivo("price_client");
    }
  };

  pickRoleAndDefaults();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // persistenza selezioni
  useEffect(() => {
    if (activeTab) localStorage.setItem("prod_tab", activeTab);
  }, [activeTab]);
  useEffect(() => {
    if (listinoAttivo) localStorage.setItem("prod_listino", listinoAttivo);
  }, [listinoAttivo]);

/* --------- Carica prodotti quando cambiano tab/listino/ruolo --------- */
useEffect(() => {
  if (!userRole || !listinoAttivo || !activeTab) return;

  // FREE: può vedere SOLO il catalogo assegnato
  if (hasFullPackage === false && activeTab === "local") {
    setActiveTab("catalogo");
    return;
  }

  fetchProdotti();
}, [userRole, listinoAttivo, activeTab, hasFullPackage]);

  const fetchProdotti = async () => {
    setCaricamento(true);
    const token = localStorage.getItem("access");
    const scope = activeTab === "local" ? "local" : "catalogo";
    const tipoListino = getTipoListinoFromValue(listinoAttivo);

    try {
      let url = `${API_URL}/api/prodotti/?scope=${scope}`;
      if (scope === "catalogo") url += `&tipo_listino=${tipoListino}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setProdotti(data);
        setCategorie([...new Set(data.map((p) => p.category).filter(Boolean))]);
      } else {
        console.error("❌ Dati non validi:", data);
        setProdotti([]);
      }
    } catch (err) {
      console.error("Errore caricamento prodotti:", err);
      setProdotti([]);
    } finally {
      setCaricamento(false);
    }
  };

  /* --------- Helpers UI --------- */
  const getQuantitaUnita = (p) => {
    const tipo = p.unit_type || p.unitType;
    return tipo === "litri"
      ? `💧 ${p.litri_per_unita || 0} L`
      : `📦 ${p.pezzi_per_unita || 0} pcs`;
  };

  // Ricava il prezzo da: p.prezzo (serializer), o dal campo p[listinoAttivo], o dall'array listini
  const getPrezzo = (p) => {
    const n = (v) => (isNaN(Number(v)) ? 0 : Number(v));
    if (p.prezzo !== undefined && p.prezzo !== null) return n(p.prezzo);

    const byKey = p[listinoAttivo];
    if (byKey !== undefined && byKey !== null) return n(byKey);

    const tipo = getTipoListinoFromValue(listinoAttivo);
    const li = (p.listini || []).find((l) => l.tipo === tipo);
    if (li && li.disponibile) return n(li.prezzo);
    return 0;
  };

  const isDisponibilePerListino = (p) => {
    if (activeTab === "local") return true; // locale: disponibilità listino non rilevante
    const tipo = getTipoListinoFromValue(listinoAttivo);
    const li = (p.listini || []).find((l) => l.tipo === tipo);
    return li ? !!li.disponibile : true; // se mancante lo considero disponibile
  };

  /* --------- Prodotti filtrati/ordinati (in memoria) --------- */
  const prodottiView = useMemo(() => {
    let arr = [...prodotti];

    // filtro ricerca
    if (q.trim()) {
      const ql = q.trim().toLowerCase();
      arr = arr.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(ql) ||
          (p.category || "").toLowerCase().includes(ql)
      );
    }

    // filtro categoria
    if (catSel !== "ALL") {
      arr = arr.filter((p) => p.category === catSel);
    }

    // filtro disponibilità per listino selezionato (solo catalogo)
    if (soloDisponibili && activeTab === "catalogo") {
      arr = arr.filter((p) => isDisponibilePerListino(p));
    }

    // ordinamento
    if (ordina === "name-asc")
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (ordina === "name-desc")
      arr.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    if (ordina === "price-asc")
      arr.sort((a, b) => getPrezzo(a) - getPrezzo(b));
    if (ordina === "price-desc")
      arr.sort((a, b) => getPrezzo(b) - getPrezzo(a));

    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodotti, q, catSel, soloDisponibili, ordina, listinoAttivo, activeTab]);

  /* --------- Form: apri/compila --------- */
  const openForm = (p = null) => {
  // 🔒 FREE: blocca subito, prima di toccare lo state del form
  if (hasFullPackage !== true) {
    alert("🔒 Funzione disponibile solo nel pacchetto FULL.");
    return;
  }

    // consentito:
    // - MADRE su catalogo
    // - ruoli ≠ MADRE su local
    const allowed =
      (canEditCatalogo && activeTab === "catalogo") ||
      (canEditLocal && activeTab === "local");
    if (!allowed) return;

    if (p) {
      setFormData({
        id: p.id,
        name: p.name,
        category: p.category,
        description: p.description || "",
        availability_status: p.availability_status || "AVAILABLE",
        unitType: p.unit_type || "litri",
        litri_per_unita: p.litri_per_unita || "",
        pezzi_per_unita: p.pezzi_per_unita || "",
        listini: ["cliente", "franchising", "azienda", "pv"].map((tipo) => {
          const esistente = (p.listini || []).find((l) => l.tipo === tipo);
          return {
            tipo,
            prezzo: esistente ? esistente.prezzo : "",
            disponibile: esistente ? !!esistente.disponibile : true,
          };
        }),
      });
    } else {
      setFormData({
        id: null,
        name: "",
        category: "",
          description: "",
        availability_status: "AVAILABLE",
        unitType: "litri",
        litri_per_unita: "",
        pezzi_per_unita: "",
        listini: [
          { tipo: "cliente", prezzo: "", disponibile: true },
          { tipo: "franchising", prezzo: "", disponibile: true },
          { tipo: "azienda", prezzo: "", disponibile: true },
          { tipo: "pv", prezzo: "", disponibile: true },
        ],
      });
    }

   setShowForm(true);


  };

  // Duplica un prodotto del catalogo nel proprio inventario locale
  const duplicaInMiei = async (p) => {
  // 🔒 FREE: blocca duplicazione
  if (hasFullPackage !== true) {
    alert("🔒 Funzione disponibile solo nel pacchetto FULL.");
    return;
  }

    if (!canEditLocal || activeTab !== "catalogo") return;
    const token = localStorage.getItem("access");
    try {
      const body = {
        name: p.name,
        category: p.category,
        unit_type: p.unit_type || "litri",
        litri_per_unita:
          (p.unit_type || "litri") === "litri" ? p.litri_per_unita || 0 : 0,
        pezzi_per_unita:
          (p.unit_type || "litri") === "pezzi" ? p.pezzi_per_unita || 0 : 0,
        // niente listini: locale e distaccato dal catalogo
      };
      const res = await fetch(`${API_URL}/api/prodotti/aggiungi/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(js.detail || "Errore duplicazione");
      alert("✅ Duplicato nei tuoi prodotti");
      setActiveTab("local");
      fetchProdotti();
    } catch (e) {
      alert("❌ " + e.message);
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
      // 🔒 FREE: blocca salvataggio
  if (hasFullPackage !== true) {
    alert("🔒 Funzione disponibile solo nel pacchetto FULL.");
    return;
  }

    const token = localStorage.getItem("access");
    const method = formData.id ? "PUT" : "POST";
    const url = formData.id
      ? `${API_URL}/api/prodotti/${formData.id}/`
      : `${API_URL}/api/prodotti/aggiungi/`;

    try {
      // Se NON MADRE o se tab=local, non inviare listini (il backend li ignorerà comunque)
      const shouldSendListini = canEditCatalogo && activeTab === "catalogo";

      const body = {
        name: formData.name,
        category: formData.category,
          description: formData.description || "",
        availability_status: formData.availability_status || "AVAILABLE",
        unit_type: formData.unitType,
        litri_per_unita: parseFloat(formData.litri_per_unita) || 0,
        pezzi_per_unita: parseInt(formData.pezzi_per_unita || 0),
        ...(shouldSendListini
          ? { listini: normalizeListini(formData.listini) }
          : {}),
      };

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("Errore:", json);
        alert(json.detail || JSON.stringify(json));
      } else {
        alert("✅ Prodotto salvato!");
      }
      setShowForm(false);
      fetchProdotti();
    } catch (err) {
      alert("Errore rete: " + err);
      console.error(err);
    }
  };

  const deleteProductFromCard = async (product) => {
    const allowedDelete =
      (canEditCatalogo && activeTab === "catalogo") ||
      (canEditLocal && activeTab === "local");
    if (!allowedDelete || !product?.id) return;

    const label = product?.name ? ` "${product.name}"` : "";
    if (!window.confirm(`Eliminare questo prodotto${label}?`)) return;

    const token = localStorage.getItem("access");
    try {
      const res = await fetch(`${API_URL}/api/prodotti/${product.id}/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) alert("✅ Prodotto eliminato.");
      else {
        const err = await res.json().catch(() => ({}));
        alert("❌ Errore: " + (err.detail || err.error || res.statusText));
      }
    } catch (err) {
      alert("❌ Errore di rete: " + err.message);
    }
    fetchProdotti();
  };

  const deleteProduct = async () => {
    const allowedDelete =
      (canEditCatalogo && activeTab === "catalogo") ||
      (canEditLocal && activeTab === "local");
    if (!allowedDelete) return;

    if (!formData.id || !window.confirm("Eliminare questo prodotto?")) return;
    const token = localStorage.getItem("access");
    try {
      const res = await fetch(`${API_URL}/api/prodotti/${formData.id}/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) alert("✅ Prodotto eliminato.");
      else {
        const err = await res.json().catch(() => ({}));
        alert("❌ Errore: " + (err.detail || err.error || res.statusText));
      }
    } catch (err) {
      alert("❌ Errore di rete: " + err.message);
    }
    setShowForm(false);
    fetchProdotti();
  };

  /* --------- Render --------- */

  const showListinoSelector = activeTab === "catalogo";
  const showAddButton =
    (activeTab === "catalogo" && canEditCatalogo) ||
    (activeTab === "local" && canEditLocal);

  return (
    <div className="container mt-4">
      {showForm ? <TornaAlMenu onClick={() => setShowForm(false)} /> : <TornaAlMenu to="/" />}

      {/* Header + Tabs */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
        <h2 className="m-0">🛒 Catalogo Prodotti</h2>
        {/* Tabs (MADRE solo Catalogo) */}
        <ul className="nav nav-pills">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "catalogo" ? "active" : ""}`}
              onClick={() => setActiveTab("catalogo")}
            >
              Listino assegnato
            </button>
          </li>
          {canSeeLocal && (
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "local" ? "active" : ""}`}
                onClick={() => setActiveTab("local")}
              >
                Miei prodotti <span className="badge bg-secondary ms-1">Locale</span>
              </button>
            </li>
          )}
        </ul>
      </div>

      {/* Barra destra (selector listino + add/annulla) */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div />
        <div className="d-flex align-items-center gap-2">
          {showListinoSelector && (
            <>
              <label className="small text-muted">Listino attivo:</label>
              <select
                className="form-select"
                style={{ width: 210 }}
                value={listinoAttivo || ""}
                onChange={(e) => setListinoAttivo(e.target.value)}
              >
                <option value="price_client">Cliente Privato</option>
                <option value="price_franchising">Franchising</option>
                <option value="price_azienda">Azienda</option>
                <option value="price_pv">Punto Vendita</option>
              </select>
            </>
          )}

          {!showForm ? (
            showAddButton && (
              <button className="btn btn-success" onClick={() => openForm()}>
                ➕ Aggiungi Prodotto
              </button>
            )
          ) : (
            <button
              className="btn btn-outline-secondary"
              onClick={() => setShowForm(false)}
            >
              ← Annulla
            </button>
          )}
        </div>
      </div>

      {/* Toolbar filtri */}
      {!showForm && (
        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-12 col-md-4">
                <label className="form-label mb-1">Cerca</label>
                <input
                  className="form-control"
                  placeholder="🔎 Nome o categoria…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label mb-1">Categoria</label>
                <select
                  className="form-select"
                  value={catSel}
                  onChange={(e) => setCatSel(e.target.value)}
                >
                  <option value="ALL">Tutte</option>
                  {categorie.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {activeTab === "catalogo" && (
                <div className="col-6 col-md-2">
                  <label className="form-label mb-1">Disponibilità</label>
                  <div className="form-check pt-1">
                    <input
                      id="soloDisp"
                      className="form-check-input"
                      type="checkbox"
                      checked={soloDisponibili}
                      onChange={(e) => setSoloDisponibili(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="soloDisp">
                      Solo disponibili
                    </label>
                  </div>
                </div>
              )}

              <div className="col-6 col-md-3">
                <label className="form-label mb-1">Ordina</label>
                <select
                  className="form-select"
                  value={ordina}
                  onChange={(e) => setOrdina(e.target.value)}
                >
                  <option value="name-asc">Nome (A → Z)</option>
                  <option value="name-desc">Nome (Z → A)</option>
                  <option value="price-asc">Prezzo (crescente)</option>
                  <option value="price-desc">Prezzo (decrescente)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FORM */}
      {showForm ? (
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white">
            <h5 className="m-0">
              {formData.id ? "✏️ Modifica Prodotto" : "🆕 Nuovo Prodotto"}
            </h5>
            <small className="text-muted">
              {activeTab === "local" && !isMadre
                ? "Prodotto locale (distaccato dal listino della SEDE)."
                : "Gestisci anagrafica e listini."}
            </small>
          </div>

          <form className="card-body" onSubmit={submitForm}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Nome *</label>
                <input
                  className="form-control"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Unità di misura</label>
                <select
                  className="form-select"
                  value={formData.unitType}
                  onChange={(e) =>
                    setFormData({ ...formData, unitType: e.target.value })
                  }
                >
                  <option value="litri">Litri per unità</option>
                  <option value="pezzi">Pezzi per unità</option>
                </select>
              </div>

              <div className="col-md-3">
                {formData.unitType === "litri" ? (
                  <>
                    <label className="form-label">Quantità (litri)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={formData.litri_per_unita}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          litri_per_unita: e.target.value,
                        })
                      }
                      required
                    />
                  </>
                ) : (
                  <>
                    <label className="form-label">Quantità (pezzi)</label>
                    <input
                      type="number"
                      step="1"
                      className="form-control"
                      value={formData.pezzi_per_unita}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pezzi_per_unita: e.target.value,
                        })
                      }
                      required
                    />
                  </>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">Categoria *</label>
                <select
                  className="form-select"
                  value={formData.category}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "__new") {
                      const nuova = prompt("Inserisci nuova categoria:");
                      if (nuova) {
                        setCategorie((prev) =>
                          Array.from(new Set([...prev, nuova]))
                        );
                        setFormData({ ...formData, category: nuova });
                      }
                    } else {
                      setFormData({ ...formData, category: val });
                    }
                  }}
                  required
                >
                  <option value="">-- Seleziona categoria --</option>
                  {categorie.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                  <option value="__new">🆕 Nuova categoria…</option>
                </select>
              </div>

                <div className="col-md-6">
                  <label className="form-label">Stato prodotto App</label>
                  <select
                    className="form-select"
                    value={formData.availability_status || "AVAILABLE"}
                    onChange={(e) =>
                      setFormData({ ...formData, availability_status: e.target.value })
                    }
                  >
                    <option value="AVAILABLE">🟢 Disponibile</option>
                    <option value="COMING_SOON">🟠 In arrivo</option>
                    <option value="UNAVAILABLE">🔴 Non disponibile</option>
                  </select>
                  <div className="form-text">
                    Il prodotto resta visibile nell\'app; se non è disponibile non sarà acquistabile.
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label">Descrizione prodotto / vino</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Descrizione visibile nell\'app cliente: profumo, gusto, abbinamenti, caratteristiche..."
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                  <div className="form-text">
                    Questa descrizione sarà mostrata nel dettaglio prodotto dell\'app cliente.
                  </div>
                </div>

              {/* Listini: visibili SOLO a MADRE su tab Catalogo */}
              {canEditCatalogo && activeTab === "catalogo" && (
                <div className="col-12">
                  <div className="row g-3">
                    {formData.listini.map((l, idx) => (
                      <div className="col-md-6" key={l.tipo}>
                        <div className="border rounded p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong className="text-uppercase">
                              {l.tipo === "cliente"
                                ? "Cliente"
                                : l.tipo === "franchising"
                                ? "Franchising"
                                : l.tipo === "azienda"
                                ? "Azienda"
                                : "Punto Vendita"}
                            </strong>
                            <div className="form-check form-switch m-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`disp-${l.tipo}`}
                                checked={l.disponibile}
                                onChange={(e) => {
                                  const copy = [...formData.listini];
                                  copy[idx].disponibile = e.target.checked;
                                  setFormData({
                                    ...formData,
                                    listini: copy,
                                  });
                                }}
                              />
                              <label
                                className="form-check-label small"
                                htmlFor={`disp-${l.tipo}`}
                              >
                                Disponibile
                              </label>
                            </div>
                          </div>
                          <label className="form-label small">Prezzo (€)</label>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            value={l.prezzo}
                            onChange={(e) => {
                              const copy = [...formData.listini];
                              copy[idx].prezzo = e.target.value;
                              setFormData({ ...formData, listini: copy });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              {formData.id && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={deleteProduct}
                >
                  Elimina
                </button>
              )}
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowForm(false)}
              >
                Annulla
              </button>
              <button type="submit" className="btn btn-primary">
                {formData.id ? "Aggiorna" : "Salva"}
              </button>
            </div>
          </form>
        </div>
      ) : caricamento ? (
        <p className="text-muted">⏳ Caricamento in corso…</p>
      ) : prodottiView.length === 0 ? (
        <div className="alert alert-info">Nessun prodotto trovato.</div>
      ) : (
        <div className="row g-3">
          {prodottiView.map((p) => {
            const disponibile = isDisponibilePerListino(p);
            const prezzo = getPrezzo(p);

            const canOpen =
              (activeTab === "catalogo" && canEditCatalogo) ||
              (activeTab === "local" && canEditLocal);

            const canDelete =
              (activeTab === "catalogo" && canEditCatalogo) ||
              (activeTab === "local" && canEditLocal);

            return (
              <div className="col-12 col-md-6 col-lg-4" key={p.id}>
                <div
                  className={`card h-100 shadow-sm border-0 ${
                    disponibile ? "" : "opacity-75"
                  }`}
                  role={canOpen ? "button" : "group"}
                  onClick={() => canOpen && openForm(p)}
                  style={{ transition: "transform .08s ease-in-out" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-2px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                >
                    <div
                      className="bg-light d-flex align-items-center justify-content-center"
                      style={{ height: 150 }}
                    >
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        />
                      ) : (
                        <div className="text-muted small text-center">
                          <div style={{ fontSize: 34 }}>🖼️</div>
                          <div>Nessuna immagine</div>
                        </div>
                      )}
                    </div>

                  <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start">
                      <h5 className="card-title text-truncate fw-bold mb-1">
                        {p.name}
                      </h5>
                      <span className="badge bg-light text-dark">
                        {getQuantitaUnita(p)}
                      </span>
                    </div>
                    <div className="text-muted small mb-2">{p.category}</div>

                    {activeTab === "catalogo" ? (
                      <div className="mt-auto d-flex align-items-center gap-2">
                        <span className="badge bg-primary">
                          💶 €{prezzo.toFixed(2)}
                        </span>
                        {disponibile ? (
                          <span className="badge bg-success">Disponibile</span>
                        ) : (
                          <span className="badge bg-secondary">Non disp.</span>
                        )}
                      </div>
                    ) : (
                      <div className="mt-auto">
                        <span className="badge bg-secondary">Locale</span>
                      </div>
                    )}
                  </div>

                  {/* Azioni card */}
                  {((activeTab === "catalogo" && !isMadre) || canDelete) && (
                    <div className="card-footer bg-white border-0 pt-0 d-flex gap-2 flex-wrap">
                      {activeTab === "catalogo" && !isMadre && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            duplicaInMiei(p);
                          }}
                        >
                          📄 Duplica in Miei prodotti
                        </button>
                      )}
                  
                      {canDelete && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteProductFromCard(p);
                          }}
                        >
                          🗑️ Elimina
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
